import { apiGet, apiPut } from "@/lib/apiClient";
import {
  defaultCrmLeadModuleFieldsDocument,
  mergeStandardCrmLeadModuleFieldSections,
  seedStandardCrmLeadModuleFields,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";

export type CrmLeadModuleFieldsResponse = {
  orgId?: string;
  sections?: CrmLeadModuleFieldsDocument["sections"];
  updatedAt?: string;
};

const LEAD_FIELDS_LOCAL_KEY = "atw-one.crm.leadModuleFields.sections";

function readLocalLeadModuleFields(): CrmLeadModuleFieldsDocument["sections"] | null {
  try {
    const raw = localStorage.getItem(LEAD_FIELDS_LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CrmLeadModuleFieldsDocument["sections"];
    return parsed?.account_lead && parsed?.contact_lead && parsed?.lead_status ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalLeadModuleFields(sections: CrmLeadModuleFieldsDocument["sections"]): void {
  try {
    localStorage.setItem(LEAD_FIELDS_LOCAL_KEY, JSON.stringify(sections));
  } catch {
    /* ignore quota / private mode */
  }
}

export async function fetchCrmLeadModuleFields(): Promise<{
  sections: CrmLeadModuleFieldsDocument["sections"];
  orgId: string | null;
  apiLive: boolean;
}> {
  const res = await apiGet<CrmLeadModuleFieldsResponse>("/api/crm/settings/modules/lead/fields");
  if (!res.ok || !res.data?.sections) {
    const local = readLocalLeadModuleFields();
    return {
      sections: mergeStandardCrmLeadModuleFieldSections(local ?? seedStandardCrmLeadModuleFields()),
      orgId: null,
      apiLive: false,
    };
  }
  const sections = mergeStandardCrmLeadModuleFieldSections(res.data.sections);
  writeLocalLeadModuleFields(sections);
  const orgId = (res.data.orgId || "").trim() || null;
  return { sections, orgId, apiLive: true };
}

export async function saveCrmLeadModuleFields(
  sections: CrmLeadModuleFieldsDocument["sections"],
): Promise<{ ok: boolean; error?: string; sections?: CrmLeadModuleFieldsDocument["sections"] }> {
  const res = await apiPut<CrmLeadModuleFieldsResponse>("/api/crm/settings/modules/lead/fields", {
    sections,
  });
  if (!res.ok) {
    writeLocalLeadModuleFields(sections);
    return {
      ok: false,
      error: res.error || "Could not save field settings. Saved in this browser until the API is live.",
    };
  }
  const next = res.data?.sections ?? sections;
  writeLocalLeadModuleFields(next);
  return { ok: true, sections: next };
}

export function emptyLeadModuleFieldsSections(): CrmLeadModuleFieldsDocument["sections"] {
  return defaultCrmLeadModuleFieldsDocument("local").sections;
}

export type { CrmLeadFieldSectionId, CrmLeadFieldDefinition, CrmLeadModuleFieldsDocument } from "@/lib/crmLeadModuleFields";
export {
  CRM_LEAD_FIELD_SECTION_IDS,
  CRM_LEAD_FIELD_SECTION_LABELS,
  seedStandardCrmLeadModuleFields,
} from "@/lib/crmLeadModuleFields";
